import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Type, AlignLeft, Hash, Mail, Phone, Calendar, ChevronDown,
  CheckSquare, Circle, Upload, PenLine, Trash2, GripVertical, Plus,
  Save, Loader2, Eye, ArrowDown, ArrowUp, FileText, Layout, ScanLine,
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import PdfLayoutDesigner from "@/components/PdfLayoutDesigner";
import { formatDate } from "@/lib/format";

const FIELD_TYPES = [
  { type: "text", label: "نص قصير", icon: Type },
  { type: "textarea", label: "نص طويل", icon: AlignLeft },
  { type: "number", label: "رقم", icon: Hash },
  { type: "email", label: "بريد إلكتروني", icon: Mail },
  { type: "phone", label: "هاتف", icon: Phone },
  { type: "date", label: "تاريخ", icon: Calendar },
  { type: "select", label: "قائمة منسدلة", icon: ChevronDown },
  { type: "checkbox", label: "خانات اختيار", icon: CheckSquare },
  { type: "radio", label: "اختيار واحد", icon: Circle },
  { type: "file", label: "رفع ملف", icon: Upload },
  { type: "ocr_image", label: "صورة + OCR", icon: ScanLine },
  { type: "signature", label: "توقيع", icon: PenLine },
];

function uuid() { return "f-" + Math.random().toString(36).slice(2, 11); }

function SortableField({ field, selected, onSelect, onDelete, onMoveUp, onMoveDown }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const ft = FIELD_TYPES.find((f) => f.type === field.field_type) || FIELD_TYPES[0];
  const Icon = ft.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onSelect(field.id)}
      data-testid={`canvas-field-${field.id}`}
      className={`group p-3 rounded-sm border cursor-pointer transition-all ${selected ? "border-primary ring-1 ring-primary bg-accent/40" : "border-border bg-card hover:border-primary/40"}`}
    >
      <div className="flex items-center gap-2">
        <button {...attributes} {...listeners} onClick={(e) => e.stopPropagation()} className="text-muted-foreground hover:text-foreground cursor-grab">
          <GripVertical className="w-4 h-4" />
        </button>
        <Icon className="w-4 h-4 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate flex items-center gap-2">
            {field.label}
            {field.is_required && <span className="text-destructive text-xs">*</span>}
          </div>
          <div className="text-xs text-muted-foreground">{ft.label}</div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={(e) => { e.stopPropagation(); onMoveUp(field.id); }} className="p-1 rounded hover:bg-surface-hover text-muted-foreground"><ArrowUp className="w-3.5 h-3.5" /></button>
          <button onClick={(e) => { e.stopPropagation(); onMoveDown(field.id); }} className="p-1 rounded hover:bg-surface-hover text-muted-foreground"><ArrowDown className="w-3.5 h-3.5" /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(field.id); }} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    </div>
  );
}

function FieldPreview({ field }) {
  const common = "w-full h-10 px-3 rounded-sm border border-border bg-background text-sm";
  switch (field.field_type) {
    case "textarea": return <textarea className={common + " h-24 py-2"} placeholder={field.placeholder} disabled />;
    case "number": return <input type="number" className={common} placeholder={field.placeholder} disabled />;
    case "email": return <input type="email" className={common} placeholder={field.placeholder} disabled />;
    case "phone": return <input type="tel" className={common} placeholder={field.placeholder} disabled />;
    case "date": return <input type="date" className={common} disabled />;
    case "select": return (
      <select className={common} disabled>
        <option>{field.placeholder || "اختر..."}</option>
        {field.options.map((o, i) => <option key={i}>{o}</option>)}
      </select>
    );
    case "checkbox": return (
      <div className="space-y-1.5">
        {field.options.map((o, i) => (
          <label key={i} className="flex items-center gap-2 text-sm">
            <input type="checkbox" disabled /> {o}
          </label>
        ))}
      </div>
    );
    case "radio": return (
      <div className="space-y-1.5">
        {field.options.map((o, i) => (
          <label key={i} className="flex items-center gap-2 text-sm">
            <input type="radio" disabled /> {o}
          </label>
        ))}
      </div>
    );
    case "file": return <input type="file" className={common + " py-2"} disabled />;
    case "ocr_image": return (
      <div className="border-2 border-dashed border-border rounded-sm p-4 text-center text-muted-foreground text-sm">
        <ScanLine className="w-6 h-6 mx-auto mb-1 text-primary/50" />
        رفع صورة واستخراج نص (OCR)
      </div>
    );
    case "signature": return <div className={common + " h-32 flex items-center justify-center text-muted-foreground"}>منطقة التوقيع</div>;
    default: return <input type="text" className={common} placeholder={field.placeholder} disabled />;
  }
}

export default function FormBuilderPage() {
  const { id: routeId } = useParams();
  const isNew = routeId === "new";
  const id = !routeId || isNew ? null : routeId;
  const navigate = useNavigate();
  const [title, setTitle] = useState("نموذج جديد");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [fields, setFields] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [templates, setTemplates] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);
  const [headerTitle, setHeaderTitle] = useState("");
  const [headerSubtitle, setHeaderSubtitle] = useState("");
  const [pdfLayout, setPdfLayout] = useState({
    mode: "default",
    page_size: "A4",
    positions: [],
    show_qr: true,
    show_form_number: true,
    show_footer: true,
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    if (!routeId) {
      api.get("/form-templates").then((r) => setTemplates(r.data)).catch(() => {});
    }
    if (id) {
      api.get(`/form-templates/${id}`).then((r) => {
        setTitle(r.data.title);
        setDescription(r.data.description || "");
        setIsActive(r.data.is_active);
        setFields(r.data.fields || []);
        setLogoUrl(r.data.logo_url || null);
        setHeaderTitle(r.data.header_title || "");
        setHeaderSubtitle(r.data.header_subtitle || "");
        setPdfLayout(r.data.pdf_layout || { mode: "default", page_size: "A4", positions: [], show_qr: true, show_form_number: true, show_footer: true });
        setLoading(false);
      }).catch((e) => { toast.error(formatApiError(e)); setLoading(false); });
    }
  }, [id, routeId]);

  const selected = fields.find((f) => f.id === selectedId);

  const addField = (type) => {
    const ft = FIELD_TYPES.find((f) => f.type === type);
    const f = {
      id: uuid(),
      label: ft.label,
      field_type: type,
      placeholder: "",
      options: ["select", "radio", "checkbox"].includes(type) ? ["خيار 1", "خيار 2"] : [],
      is_required: false,
      sort_order: fields.length,
      validations: {},
      ocr_prompt: type === "ocr_image" ? "استخرج جميع النصوص والبيانات من هذه الوثيقة بدقة" : null,
    };
    setFields([...fields, f]);
    setSelectedId(f.id);
  };

  const updateField = (patch) => {
    setFields(fields.map((f) => (f.id === selectedId ? { ...f, ...patch } : f)));
  };

  const deleteField = (fid) => {
    setFields(fields.filter((f) => f.id !== fid));
    if (selectedId === fid) setSelectedId(null);
  };

  const moveBy = (fid, delta) => {
    const idx = fields.findIndex((f) => f.id === fid);
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= fields.length) return;
    setFields(arrayMove(fields, idx, newIdx));
  };

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = fields.findIndex((f) => f.id === active.id);
    const newIdx = fields.findIndex((f) => f.id === over.id);
    setFields(arrayMove(fields, oldIdx, newIdx));
  };

  const save = async () => {
    if (!title.trim()) return toast.error("أدخل عنوان النموذج");
    setSaving(true);
    try {
      const cleanFields = fields.map((f) => ({
        ...f,
        options: (f.options || []).map((o) => (o || "").trim()).filter(Boolean),
      }));
      const validIds = new Set(cleanFields.map((f) => f.id));
      const cleanLayout = {
        ...pdfLayout,
        positions: (pdfLayout.positions || []).filter((p) => validIds.has(p.field_id)),
      };
      const payload = {
        title,
        description,
        is_active: isActive,
        fields: cleanFields,
        logo_url: logoUrl,
        header_title: headerTitle || title,
        header_subtitle: headerSubtitle,
        pdf_layout: cleanLayout,
      };
      if (id) {
        await api.put(`/form-templates/${id}`, payload);
        toast.success("تم حفظ النموذج");
      } else {
        const { data } = await api.post("/form-templates", payload);
        toast.success("تم إنشاء النموذج");
        navigate(`/form-builder/${data.id}`, { replace: true });
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!id) return;
    if (!window.confirm("حذف هذا النموذج؟")) return;
    try {
      await api.delete(`/form-templates/${id}`);
      toast.success("تم الحذف");
      navigate("/form-builder");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) {
    return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  }

  // List view (no routeId)
  if (!routeId) {
    return (
      <div data-testid="form-builder-list">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">منشئ النماذج</h1>
            <p className="text-muted-foreground mt-1">صمّم نماذج مخصصة بالسحب والإفلات</p>
          </div>
          <Button onClick={() => navigate("/form-builder/new")} data-testid="new-template-button" className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 ms-1" /> نموذج جديد
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => navigate(`/form-builder/${t.id}`)}
              data-testid={`template-card-${t.id}`}
              className="text-start gov-card p-5 hover:border-primary/40 transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="font-display font-semibold text-lg">{t.title}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${t.is_active ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                  {t.is_active ? "نشط" : "غير نشط"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{t.description || "—"}</p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{t.fields?.length || 0} حقل</span>
                <span className="font-mono">{new Date(t.updated_at).toLocaleDateString("ar-EG")}</span>
              </div>
            </button>
          ))}
          {!templates.length && (
            <div className="col-span-full gov-card p-12 text-center text-muted-foreground">
              لا توجد نماذج بعد. ابدأ بإنشاء نموذج جديد.
            </div>
          )}
        </div>
      </div>
    );
  }

  // Builder view
  return (
    <div data-testid="form-builder-page">
      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="flex-1 max-w-xl">
          <Input
            data-testid="template-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="text-2xl font-display font-bold border-0 px-0 h-auto py-1 shadow-none focus-visible:ring-0 bg-transparent"
            placeholder="عنوان النموذج"
          />
          <Input
            data-testid="template-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="text-sm text-muted-foreground border-0 px-0 h-auto py-1 shadow-none focus-visible:ring-0 bg-transparent"
            placeholder="وصف النموذج (اختياري)"
          />
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={isActive} onCheckedChange={setIsActive} data-testid="template-active-switch" />
            <span>نشط</span>
          </label>
          {!isNew && id && (
            <Button variant="outline" onClick={remove} data-testid="delete-template-button" className="text-destructive hover:text-destructive">
              <Trash2 className="w-4 h-4 ms-1" /> حذف
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="save-template-button" className="bg-primary hover:bg-primary/90">
            {saving ? <Loader2 className="w-4 h-4 ms-2 animate-spin" /> : <Save className="w-4 h-4 ms-2" />}
            حفظ
          </Button>
        </div>
      </div>

      <Tabs defaultValue="fields" className="w-full" data-testid="builder-tabs">
        <TabsList className="mb-4">
          <TabsTrigger value="fields" data-testid="tab-fields">
            <FileText className="w-4 h-4 ms-1.5" /> حقول النموذج
          </TabsTrigger>
          <TabsTrigger value="pdf" data-testid="tab-pdf">
            <Layout className="w-4 h-4 ms-1.5" /> إعدادات الطباعة (PDF)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="fields">
          <div className="grid grid-cols-12 gap-4 min-h-[600px]">
        {/* Palette */}
        <div className="col-span-12 lg:col-span-3 gov-card overflow-hidden" data-testid="form-builder-palette">
          <div className="px-4 py-3 border-b border-border bg-surface text-xs uppercase tracking-wider text-muted-foreground font-medium">
            أنواع الحقول
          </div>
          <div className="p-3 grid grid-cols-2 lg:grid-cols-1 gap-2">
            {FIELD_TYPES.map((ft) => {
              const Icon = ft.icon;
              return (
                <button
                  key={ft.type}
                  onClick={() => addField(ft.type)}
                  data-testid={`palette-add-${ft.type}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-sm border border-border bg-card hover:border-primary hover:bg-accent transition-all text-sm text-start"
                >
                  <Icon className="w-4 h-4 text-primary" />
                  {ft.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Canvas */}
        <div className="col-span-12 lg:col-span-6 gov-card overflow-hidden" data-testid="form-builder-canvas">
          <div className="px-4 py-3 border-b border-border bg-surface text-xs uppercase tracking-wider text-muted-foreground font-medium flex items-center justify-between">
            <span>المعاينة المباشرة</span>
            <span className="font-mono-num">{fields.length} حقل</span>
          </div>
          <div className="p-5">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2.5">
                  {fields.map((f) => (
                    <SortableField
                      key={f.id}
                      field={f}
                      selected={selectedId === f.id}
                      onSelect={setSelectedId}
                      onDelete={deleteField}
                      onMoveUp={(fid) => moveBy(fid, -1)}
                      onMoveDown={(fid) => moveBy(fid, 1)}
                    />
                  ))}
                  {!fields.length && (
                    <div className="border-2 border-dashed border-border rounded-sm p-12 text-center text-muted-foreground">
                      <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      اختر نوع حقل من اللوحة لإضافته
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* Properties */}
        <div className="col-span-12 lg:col-span-3 gov-card overflow-hidden" data-testid="form-builder-properties">
          <div className="px-4 py-3 border-b border-border bg-surface text-xs uppercase tracking-wider text-muted-foreground font-medium">
            خصائص الحقل
          </div>
          <div className="p-4 space-y-3">
            {!selected && <div className="text-sm text-muted-foreground text-center py-8">اختر حقلاً لتعديل خصائصه</div>}
            {selected && (
              <>
                <div className="space-y-1.5">
                  <Label>العنوان</Label>
                  <Input data-testid="prop-label" value={selected.label} onChange={(e) => updateField({ label: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>النص التوضيحي</Label>
                  <Input data-testid="prop-placeholder" value={selected.placeholder || ""} onChange={(e) => updateField({ placeholder: e.target.value })} />
                </div>
                <label className="flex items-center justify-between text-sm pt-2">
                  <span>مطلوب</span>
                  <Switch data-testid="prop-required" checked={selected.is_required} onCheckedChange={(c) => updateField({ is_required: c })} />
                </label>

                {["select", "radio", "checkbox"].includes(selected.field_type) && (
                  <div className="space-y-1.5">
                    <Label>الخيارات (سطر لكل خيار)</Label>
                    <Textarea
                      data-testid="prop-options"
                      rows={5}
                      value={selected.options.join("\n")}
                      onChange={(e) => updateField({ options: e.target.value.split("\n") })}
                      onBlur={(e) => updateField({ options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    />
                  </div>
                )}

                {selected.field_type === "ocr_image" && (
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1">
                      <ScanLine className="w-3.5 h-3.5 text-primary" /> نص توجيه OCR
                    </Label>
                    <Textarea
                      data-testid="prop-ocr-prompt"
                      rows={4}
                      value={selected.ocr_prompt || ""}
                      onChange={(e) => updateField({ ocr_prompt: e.target.value })}
                      placeholder="مثال: استخرج الاسم ورقم الجواز وتاريخ الميلاد وتاريخ الانتهاء"
                      className="text-xs"
                    />
                    <p className="text-xs text-muted-foreground">يُرسَل هذا النص لنموذج الذكاء الاصطناعي عند رفع الصورة لتوجيه عملية الاستخراج</p>
                  </div>
                )}

                <div className="border-t border-border pt-3 mt-2">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">معاينة</div>
                  <FieldPreview field={selected} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
        </TabsContent>

        <TabsContent value="pdf">
          <div className="space-y-6">
            {/* Header customization */}
            <div className="gov-card p-5">
              <div className="font-display text-lg font-semibold mb-4">ترويسة وثيقة الـ PDF</div>
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 md:col-span-4">
                  <Label className="text-sm mb-2 block">شعار الوثيقة</Label>
                  <ImageUpload
                    value={logoUrl}
                    onChange={setLogoUrl}
                    label="ارفع شعاراً"
                    testid="logo-upload"
                    height={140}
                  />
                  <p className="text-xs text-muted-foreground mt-2">يظهر في رأس صفحة الـ PDF</p>
                </div>
                <div className="col-span-12 md:col-span-8 space-y-4">
                  <div>
                    <Label className="text-sm">عنوان رأس الوثيقة</Label>
                    <Input
                      data-testid="header-title-input"
                      value={headerTitle}
                      onChange={(e) => setHeaderTitle(e.target.value)}
                      placeholder={title || "عنوان الوثيقة"}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">العنوان الفرعي / تذييل الترويسة</Label>
                    <Input
                      data-testid="header-subtitle-input"
                      value={headerSubtitle}
                      onChange={(e) => setHeaderSubtitle(e.target.value)}
                      placeholder="مثال: PASSPORT DEPARTMENT"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Layout mode toggle */}
            <div className="gov-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-display text-lg font-semibold">تخطيط حقول الـ PDF</div>
                  <p className="text-xs text-muted-foreground mt-0.5">اختر بين تخطيط افتراضي بسيط أو تخطيط مخصص بالسحب والإفلات</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setPdfLayout({ ...pdfLayout, mode: "default" })}
                    data-testid="layout-mode-default"
                    className={`px-4 py-2 rounded-sm border text-sm transition-all ${pdfLayout.mode === "default" ? "border-primary bg-accent text-primary font-medium" : "border-border hover:bg-surface-hover"}`}
                  >
                    افتراضي (جدول)
                  </button>
                  <button
                    type="button"
                    onClick={() => setPdfLayout({ ...pdfLayout, mode: "custom" })}
                    data-testid="layout-mode-custom"
                    className={`px-4 py-2 rounded-sm border text-sm transition-all ${pdfLayout.mode === "custom" ? "border-primary bg-accent text-primary font-medium" : "border-border hover:bg-surface-hover"}`}
                  >
                    مخصص (سحب وإفلات)
                  </button>
                </div>
              </div>

              {pdfLayout.mode === "default" ? (
                <div className="rounded-sm border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground">
                  سيتم رسم الحقول كجدول مرتب تلقائياً (تسمية + قيمة) كما في التصميم القياسي للوثيقة الرسمية.
                </div>
              ) : (
                <PdfLayoutDesigner
                  fields={fields}
                  layout={pdfLayout}
                  onChange={setPdfLayout}
                />
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
